/**
 * The app path this TAB was on, so a re-login that lands the user on `/` can send
 * them back where they were (multi-org users pass through /select-org, which drops
 * the server-side `redirect` — this is the client-side backstop for that hop).
 *
 * sessionStorage, not localStorage: the value is per-tab, so it restores THIS tab's
 * page and never leaks one tab's location into another. It survives the full-page
 * Keycloak round-trip within the same tab and is gone once the tab closes.
 *
 * Cleared on logout via clearSessionLocalState() so a new identity on the same tab
 * never inherits the previous user's page. Every access is try/catch'd: private mode,
 * blocked site data and SSR throw or lack `window`, and must degrade to "no restore".
 */

// sessionStorage intentionally — a per-tab UI convenience (which page to return to),
// not cross-boundary or server state. Same sanctioned exception as the permission
// snapshot in clear-session-local-state.ts. Ref: state-management.md <forbidden_patterns>.
const KEY = 'scorpio:return-to'

function storage(): Storage | null {
  try {
    return typeof window === 'undefined' ? null : window.sessionStorage
  } catch {
    return null
  }
}

export function readReturnPath(): string | null {
  try {
    return storage()?.getItem(KEY) || null
  } catch {
    return null
  }
}

export function writeReturnPath(path: string): void {
  if (!path) return
  try {
    storage()?.setItem(KEY, path)
  } catch {
    // Quota / blocked storage — restore just won't happen; no functional loss.
  }
}

export function clearReturnPath(): void {
  try {
    storage()?.removeItem(KEY)
  } catch {
    // Best-effort.
  }
}
